import React from 'react';
import api from '../../services/api';
import './leadSources.css';

// Admin page for lead sources, so they can be added without an engineer.
//
// The Value/Label split drives the whole UI. Value is what gets stored on a job
// and what automation conditions match on, so it is shown but never editable
// after creation — changing it would orphan every project already carrying it and
// silently stop those rules matching. Renaming is a Label change.
function LeadSources() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [newLabel, setNewLabel] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get('/lead-sources', { params: { includeInactive: true } })
      .then((res) => setItems(res.data || []))
      .catch(() => setError('Could not load lead sources.'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/lead-sources', { label });
      setNewLabel('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add that lead source.');
    } finally {
      setSaving(false);
    }
  };

  const rename = async (row, label) => {
    if (!label.trim() || label === row.label) return;
    try {
      await api.put(`/lead-sources/${row.id}`, {
        label: label.trim(), sortOrder: row.sortOrder, isActive: row.isActive,
      });
      load();
    } catch {
      setError('Could not rename that lead source.');
    }
  };

  const setActive = async (row, isActive) => {
    try {
      await api.put(`/lead-sources/${row.id}`, {
        label: row.label, sortOrder: row.sortOrder, isActive,
      });
      load();
    } catch {
      setError('Could not change that lead source.');
    }
  };

  if (loading) return <div className="ls-loading">Loading lead sources…</div>;

  return (
    <div className="ls-page">
      <h2 className="ls-title">Lead Sources</h2>
      <p className="ls-intro">
        These appear in the Lead Source dropdown on a job. Retiring one keeps it on
        jobs that already use it — it just stops being offered on new work.
      </p>

      {error && <div className="ls-error">{error}</div>}

      <form className="ls-add" onSubmit={add}>
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New lead source (e.g. Facebook)"
          maxLength={100}
          className="form-input"
        />
        <button type="submit" className="ls-add-btn" disabled={saving || !newLabel.trim()}>
          {saving ? 'Adding…' : 'Add'}
        </button>
      </form>

      <table className="ls-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Stored value</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className={row.isActive ? '' : 'ls-retired'}>
              <td>
                <input
                  type="text"
                  defaultValue={row.label}
                  onBlur={(e) => rename(row, e.target.value)}
                  className="ls-inline-input"
                  maxLength={100}
                />
              </td>
              <td>
                {/* Shown because automations match on it, and someone building a
                    rule needs to know the exact string. Not editable: see above. */}
                <code className="ls-value">{row.value}</code>
              </td>
              <td>{row.isActive ? 'Active' : 'Retired'}</td>
              <td className="ls-actions">
                <button
                  type="button"
                  className="ls-toggle"
                  onClick={() => setActive(row, !row.isActive)}
                >
                  {row.isActive ? 'Retire' : 'Restore'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default LeadSources;
